// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import getCustomRealTimeWarehouseInventory from "@stoneage/App/Handlers/CustomRealTimeInventory/CustomRealTimeInventory";
import { ExtendedResult } from "@stoneage/App/Handlers/RealTimeInventory/GetRealTimeWarehouseInventory";
import { isDealer } from "@stoneage/App/Store/Selectors/GeneralSelectors";
import IconAngleDownActiveCircle from "@stoneage/XDL/Icons/IconAngleDownActiveCircle";
import IconAngleUpCircle from "@stoneage/XDL/Icons/IconAngleUpCircle";
import IconArrowCurved from "@stoneage/XDL/Icons/IconArrowCurved";
import IconTimer from "@stoneage/XDL/Icons/IconTimer";
import { fontConfig } from "@stoneage/XDL/constants";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, useTheme } from "styled-components";

// XNG-Changes
interface BranchInfo {
    name: string;
    description: string;
    qtyOnHand: number;
}

// XNG-Changes: custom warehouse shape that can include branches
interface CustomWarehouseDto extends WarehouseDto {
    branches?: BranchInfo[];
}

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    warehouseAvailability?: WarehouseDto[];

    isSingleProduct?: boolean;
    isInCatalog?: boolean;
    productNumber?: string;
    components?: [];
    customInventoryCall?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    // XNG-Changes: check if current customer is a dealer
    isDealer: isDealer(state),
    configurationSelection: state.pages.productDetails.configurationSelection,
    configurationCompleted: state.pages.productDetails.configurationCompleted,
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
    getCustomRealTimeWarehouseInventory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    // XNG-Changes
    warehouseNameTableCell?: DataTableCellBaseProps;
    warehouseQTYTableCell?: DataTableCellBaseProps;
    availabilityByBranchTableRow?: DataTableRowProps;
    branchNameTableCell?: DataTableCellBaseProps;
    branchQTYTableCell?: DataTableCellBaseProps;
    toggleIcon?: IconPresentationProps;
    branchArrowIcon?: IconPresentationProps;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
        `,
    },
    messageGridItem: { width: 12 },
    availabilityByWarehouseLinkGridItem: { width: 12 },
    availabilityByWarehouseModal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        size: 12,
        weight: fontConfig.weight.medium,
        color: "text.main",
        css: css`
            width: 100%;
            white-space: nowrap;
            ${wrapInContainerStyles}
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
        size: "12px",
        lineHeight: "16px",
        css: css`
            white-space: nowrap;
        `,
    },
};

const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    /* padding: 5px; */
    /* border-radius: 5px; */
    /* background-color: ${props => safeColor(resolveColor(props.color, props.theme)).rgb().alpha(0.2).string()}; */
    ${IconWrapper} {
        margin-right: 4px;
    }
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return IconTimer;
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return AlertTriangle;
    }

    return Check;
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
    productId?: string | null,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse ||
        !productId
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    // XNG-Changes
    isDealer,
    warehouseAvailability,
    isSingleProduct,
    isInCatalog,
    productNumber,
    components,
    customInventoryCall,
    getCustomRealTimeWarehouseInventory,
    // XNG-Changes
    configurationSelection,
}: Props) => {
    // XNG-Changes: to get color for gray - transparent background
    const theme = useTheme() as BaseTheme | undefined;
    const rowGrayBackground = theme ? resolveColor("common.ui4", theme) : "#f3f3f3";

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<CustomWarehouseDto[] | undefined>(undefined);
    // XNG-Changes
    const [branches, setBranches] = useState<Record<string, BranchInfo[]> | null>(null);
    const [expandedWarehouses, setExpandedWarehouses] = useState<Record<string, boolean>>({});

    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        if (!isInCatalog && customInventoryCall) {
            // do custom call to get the inventory warehouses
            getCustomRealTimeWarehouseInventory({
                productNumber: productNumber || "",
                isSingleProduct: isSingleProduct || false,
                components: components || [],
                onSuccess: result => {
                    setWarehouses(result.inventories as CustomWarehouseDto[]);
                    setErrorMessage(result.errorMessage);
                    setIsLoading(false);
                },
            });
        } else {
            const sectionOptions =
                configurationSelection && Object.values(configurationSelection)?.length > 0
                    ? (Object.values(configurationSelection).filter(sectionOptionId => !!sectionOptionId) as string[])
                    : undefined;

            getRealTimeWarehouseInventory({
                productId,
                unitOfMeasure,
                expand: ["warehouses"],
                // XNG-Changes
                ...(sectionOptions?.length && { configuration: sectionOptions }),
                onComplete: (result: ExtendedResult) => {
                    if (result.branches) {
                        const branches = JSON.parse(result.branches);
                        setBranches(branches);
                    }

                    setWarehouses(result.warehouses);
                    setErrorMessage(result.errorMessage);
                    setIsLoading(false);
                },
            });
        }
    };

    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));
    if (!showInventory) {
        return null;
    }

    // Happens only on PDP when the variantChild/configuration has changed, the msg type might not be 0 and the
    // product might have inventory
    if ((failedToLoadInventory || !availability) && (isProductDetailsPage ? availability?.messageType === 0 : true)) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed")}</Typography>
            </StyledWrapper>
        );
    }

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
        inventoryTextComponent = (
            <InventoryMessage color={color} {...styles.inventoryMessage}>
                <Icon size={12} color="text.main" src={iconSrc} />
                <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                    {availability.message}
                </Typography>
            </InventoryMessage>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }
    // XNG-Changes
    const toggleExpand = (warehouseName: string) => {
        setExpandedWarehouses(prev => ({
            ...prev,
            [warehouseName]: !prev[warehouseName],
        }));
    };

    return (
        <GridContainer {...styles.container}>
            {isDealer &&
            (showLink(availability, trackInventory, productSettings, isProductDetailsPage, productId) ||
                (!isInCatalog && customInventoryCall)) ? (
                <GridItem
                    {...styles.availabilityByWarehouseLinkGridItem}
                    data-test-selector="availabilityByWarehouseLinkGridItem"
                >
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                    >
                        {inventoryTextComponent}
                    </Link>
                    <Modal
                        {...styles.availabilityByWarehouseModal}
                        headline={translate("Warehouse Inventory")}
                        isOpen={modalIsOpen}
                        handleClose={modalCloseHandler}
                    >
                        {isLoading && (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        )}
                        {!isLoading && (
                            <>
                                {errorMessage ? (
                                    <div>{errorMessage}</div>
                                ) : (
                                    <DataTable
                                        {...styles.availabilityByWarehouseTable}
                                        data-test-selector="availabilityByWarehouseModal"
                                    >
                                        <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("Warehouse")}
                                            </DataTableHeader>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("QTY")}
                                            </DataTableHeader>
                                        </DataTableHead>
                                        <DataTableBody translate="no" {...styles.availabilityByWarehouseTableBody}>
                                            {/* XNG-Changes : add branches - qty under warehouses */}
                                            {warehouses?.map((warehouse, index) => {
                                                const resolvedBranches =
                                                    !isInCatalog && customInventoryCall
                                                        ? (warehouse.branches ?? [])
                                                        : branches?.[warehouse.name];

                                                const hasManyBranches = resolvedBranches && resolvedBranches.length > 0;
                                                const isExpanded = expandedWarehouses[warehouse.name];
                                                const rowBackground =
                                                    index % 2 === 0 ? rowGrayBackground : "transparent";

                                                return (
                                                    <React.Fragment key={warehouse.name}>
                                                        <DataTableRow
                                                            {...styles.availabilityByWarehouseTableRow}
                                                            key={warehouse.name}
                                                            style={{ backgroundColor: rowBackground }}
                                                        >
                                                            <DataTableCell
                                                                {...styles.warehouseNameTableCell}
                                                                data-test-selector="warehouseAvailabilityModal_Description"
                                                            >
                                                                {hasManyBranches && (
                                                                    <Icon
                                                                        width={14}
                                                                        height={22}
                                                                        color="text.main"
                                                                        {...styles.toggleIcon}
                                                                        src={
                                                                            isExpanded
                                                                                ? IconAngleUpCircle
                                                                                : IconAngleDownActiveCircle
                                                                        }
                                                                        onClick={() => toggleExpand(warehouse.name)}
                                                                    />
                                                                )}
                                                                {warehouse.description || warehouse.name}
                                                            </DataTableCell>
                                                            <DataTableCell
                                                                {...styles.warehouseQTYTableCell}
                                                                data-test-selector="warehouseAvailabilityModal_Quantity"
                                                            >
                                                                {warehouse.qty}
                                                            </DataTableCell>
                                                        </DataTableRow>
                                                        {isExpanded &&
                                                            resolvedBranches?.map(branch => (
                                                                <DataTableRow
                                                                    {...styles.availabilityByBranchTableRow}
                                                                    key={branch.name}
                                                                    style={{ backgroundColor: rowBackground }}
                                                                >
                                                                    <DataTableCell
                                                                        {...styles.branchNameTableCell}
                                                                        data-test-selector="branchAvailabilityModal_Description"
                                                                    >
                                                                        <Icon
                                                                            width={14}
                                                                            height={22}
                                                                            color="text.main"
                                                                            {...styles.branchArrowIcon}
                                                                            src={IconArrowCurved}
                                                                        />
                                                                        {branch.description || branch.name}
                                                                    </DataTableCell>
                                                                    <DataTableCell
                                                                        {...styles.branchQTYTableCell}
                                                                        data-test-selector="branchAvailabilityModal_Quantity"
                                                                    >
                                                                        {branch.qtyOnHand}
                                                                    </DataTableCell>
                                                                </DataTableRow>
                                                            ))}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </DataTableBody>
                                    </DataTable>
                                )}
                            </>
                        )}
                    </Modal>
                </GridItem>
            ) : (
                <GridItem {...styles.messageGridItem} data-test-selector="ProductAvailability-messageGridItem">
                    {inventoryTextComponent}
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
